import { AntDesign, MaterialIcons } from "@expo/vector-icons";
import { BlurView } from "expo-blur";
import { router } from "expo-router";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { View, Modal, Pressable, Text } from "react-native";
import { ActivityIndicator } from "react-native-paper";

import Subtitle from "@/components/Subtitle";
import Title from "@/components/Title";
import ButtonSmall from "@/components/button/ButtonSmall";
import InputChoose from "@/components/input/InputChoose";
import { sendRequest } from "@/utils/ApiUtil";
import { BridgeVirtualAccountPaymentCurrency } from "@/utils/dto/extracted_enums";
import {
  convertDepositInstructionsToCounterpartyFormat,
  copyToClipboard,
  DepositInstructions,
  renderDepositInstructions,
} from "@/utils/web3/Utils";

interface FiatToCryptoDepositModalProps {
  visible: boolean;
  onClick?: (state: boolean) => void;
  onClose: () => void;
  walletAddress: string;
  rail: string;
}

interface Counterparty {
  counterpartyType: string;
  rails: string[];
  profile: {
    profileType: string;
    name: string;
    relationshipToCustomer: string;
    address: {
      addressLine1: string;
      city: string;
      state: string;
      countryCode: string;
      postalCode: string;
    };
  };
  accountInformation: {
    type: string;
    accountNumber: string;
    routingNumber: string;
    institutionName: string;
    institutionAddress: {
      addressLine1: string;
      city: string;
      state: string;
      countryCode: string;
      postalCode: string;
    };
  };
}

export default function FiatToCryptoDepositModal(
  props: FiatToCryptoDepositModalProps
) {
  const [depositInstructions, setDepositInstructions] = useState<any>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorToSubmit, setErrorToSubmit] = useState(false);
  const [copied, setCopied] = useState(false);
  const [creatingDepositInstructions, setCreatingDepositInstructions] =
    useState(false);
  const [currency, setCurrency] = useState("");

  const { t } = useTranslation();
  const [requestMessage, setRequestMessage] = useState(
    t("home/modals:cryptodeposit.sendwithizzi")
  );

  const depositKeysToIgnore = ["customerId"];

  const depositKeysToDelete = [
    "paymentRail",
    "createdAt",
    "version",
    "updatedAt",
    "deletedAt",
    "id",
  ];

  const { control } = useForm<FormData>({
    mode: "onSubmit",
  });

  const clearStates = () => {
    setSubmitting(false);
    setSubmitted(false);
    setErrorToSubmit(false);
    setCopied(false);
    setCurrency("");
    setDepositInstructions({});
  };

  useEffect(() => {
    if (!submitted && !submitting && !errorToSubmit) {
      setRequestMessage("");
    } else if (submitted) {
      setRequestMessage(t("home/modals/cryptodeposit:success"));
    } else if (errorToSubmit) {
      setRequestMessage(t("home/modals/cryptodeposit:error"));
    } else if (submitting) {
      setRequestMessage(t("home/modals/cryptodeposit:sending"));
    }
  }, [submitting, submitted, errorToSubmit]);

  const getDepositInstructions = async (
    address: string,
    payment_rail: string,
    currency: string
  ) => {
    try {
      setCreatingDepositInstructions(true);
      const requestData = {
        address,
        payment_rail,
        currency,
      };
      const request = await sendRequest(
        "get",
        "/bridge/virtual-account",
        requestData,
        true,
        true
      );
      depositKeysToDelete.forEach((key) => {
        if (key in request.virtualAccount?.sourceDepositInstructions) {
          delete request.virtualAccount?.sourceDepositInstructions[key];
        }
      });
      const data: DepositInstructions = {
        ...request.virtualAccount?.sourceDepositInstructions,
        customerId: request.virtualAccount.customerId,
      };
      setDepositInstructions(data);
    } catch (error: any) {
      console.error(error);
      props.onClose();
    } finally {
      setCreatingDepositInstructions(false);
    }
  };

  const getCustomer = async (bridgeCustomerId: string) => {
    try {
      const request = await sendRequest(
        "get",
        "/bridge/customer",
        { bridgeCustomerId },
        true,
        true
      );
      return request;
    } catch (error: any) {
      console.error(error);
      props.onClose();
    }
  };

  //Take Bridge deposit instructions and return Counterparty format to submit
  const createDepositAsCounterparty = async (
    depositInstructions: DepositInstructions
  ) => {
    try {
      const customerData = await getCustomer(depositInstructions.customerId);

      const counterparty = convertDepositInstructionsToCounterpartyFormat(
        depositInstructions,
        customerData
      );

      return counterparty;
    } catch (error: any) {
      console.error(error);
    }
  };

  //Submit the Bridge deposit instructions to Counterparty
  const submitCounterparty = async (counterparty: Counterparty) => {
    setErrorToSubmit(false);
    setSubmitted(false);
    try {
      setSubmitting(true);
      const request = await sendRequest(
        "POST",
        "/counterparty",
        counterparty,
        true
      );
      setSubmitted(true);
      return request;
    } catch (error: any) {
      console.error(error);
      setErrorToSubmit(true);
      props.onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      onDismiss={() => {
        clearStates();
        setDepositInstructions({});
      }}
      visible={props.visible}
      transparent
      animationType="fade"
    >
      <BlurView intensity={20} className="flex-1 justify-center items-center">
        <View className="p-6 bg-white1 border border-gray rounded-lg w-full h-full items-center justify-center shadow relative lg:w-104 lg:h-136 lg:justify-start">
          <Pressable
            className="absolute right-3 top-3 z-50"
            onPress={props.onClose}
          >
            <AntDesign name="close" size={24} color="black" />
          </Pressable>
          <View className="flex-grow items-center justify-center lg:justify-start">
            <View className="m-1 gap-2">
              <Title text={t("home/modals/cryptodeposit:title")} />
              <Subtitle text={t("home/modals/cryptodeposit:subtitle")} />
            </View>
            <View className="w-full items-center px-4 mt-4 z-50">
              {!currency && (
                <View className="z-50">
                  <InputChoose
                    label={t("home/modals/cryptodeposit:input.currency")}
                    control={control}
                    name="accountType"
                    handleChange={(data) => {
                      setCurrency(data);
                      getDepositInstructions(
                        props.walletAddress,
                        props.rail,
                        data
                      );
                    }}
                    data={{ BridgeVirtualAccountPaymentCurrency }}
                  />
                </View>
              )}
              {creatingDepositInstructions && (
                <View className="flex-col gap-2 items-center my-2">
                  <ActivityIndicator size="large" color="#5D0CBA" />
                  <Text className="text-base font-light">
                    {t("home/modals/cryptodeposit:creatingdeposit")}
                  </Text>
                </View>
              )}
              {Object.keys(depositInstructions).length > 0 &&
                !creatingDepositInstructions && (
                  <View className="w-full items-center gap-2">
                    {renderDepositInstructions(
                      depositInstructions,
                      t,
                      depositKeysToIgnore
                    )}
                    <Pressable
                      className="flex-row items-center gap-2 my-2"
                      onPress={() => {
                        const depositWithoutCustomerId = depositInstructions;
                        if (depositWithoutCustomerId["customerId"]) {
                          delete depositWithoutCustomerId["customerId"];
                        }
                        copyToClipboard(depositWithoutCustomerId);
                        setCopied(true);
                      }}
                    >
                      {copied ? (
                        <AntDesign name="check" size={18} color="green" />
                      ) : (
                        <MaterialIcons
                          name="content-copy"
                          size={18}
                          color="#919191"
                        />
                      )}
                      <Text className="text-sm text-darkergray">
                        {t("home/modals/cryptodeposit:copytoclipboard")}
                      </Text>
                    </Pressable>
                    {!errorToSubmit && (
                      <ButtonSmall
                        label={
                          submitted
                            ? t("common:button.seebeneficiaries")
                            : t("common:button.sendusingizzi")
                        }
                        onClick={async () => {
                          if (submitted) {
                            props.onClose();
                            router.push("/home/transaction/contacts");
                          } else {
                            await createDepositAsCounterparty(
                              depositInstructions
                            ).then((counterparty) => {
                              if (counterparty) {
                                submitCounterparty(counterparty);
                              }
                            });
                          }
                        }}
                        isLoading={submitting}
                      />
                    )}
                    <View className="flex-col mt-1 gap-2">
                      <View className="flex-row items-center gap-1">
                        <Text className="text-sm text-center font-light">
                          {requestMessage}
                        </Text>
                        {submitted && (
                          <AntDesign name="check" size={20} color="green" />
                        )}
                      </View>
                    </View>
                  </View>
                )}
            </View>
          </View>
        </View>
      </BlurView>
    </Modal>
  );
}
